import {
  IBayPattern,
  IRowPattern,
  ITierPattern,
  PairedStackContainer,
  SmartPairedBayBlock,
  SmartUnpairedBayBlock,
  TIContainerInformationExtendedValid,
  UnpairedStackContainer,
} from "@tedivo/tedivo-bay-grid-pure";

type TBlock = SmartUnpairedBayBlock | SmartPairedBayBlock;

type TGridContent = Record<
  IRowPattern,
  Record<ITierPattern, UnpairedStackContainer | PairedStackContainer>
>;

/** The bays a container's footprint covers: its own for a 20, both odd bays of the pair for a 40 */
export function baysCoveredBy(
  c: TIContainerInformationExtendedValid,
  block: TBlock,
): IBayPattern[] {
  if (Number(c.bay) % 2 === 1) return [c.bay];

  const oddBays = block.getEnclosedBays().filter((b) => Number(b) % 2 === 1);
  return oddBays.length > 0 ? oddBays : [c.bay];
}

/**
 * The containers seen from above (`"highest"`) or from below (`"lowest"`) in one
 * stack of a block, per 20' footprint.
 *
 * A paired block holds two 20' bays side by side, so a row of it is two stacks
 * that need not be equally tall. Taking only the top tier of the row dropped
 * the 20 of the shorter bay (e.g. 005 topped at 22 next to 007 topped at 24).
 * Here every odd bay gets the outermost container over it, and a 40 counts for
 * both. A 40 with only one bay taken by a 20 further out still shows its other
 * half, so it is kept.
 */
export function topContainersOfStack(
  block: TBlock,
  gridContent: TGridContent,
  stackKey: IRowPattern,
  allTiersKeys: ITierPattern[],
  from: "highest" | "lowest",
): TIContainerInformationExtendedValid[] {
  const tiersInStack = allTiersKeys
    .filter((tier) => gridContent[stackKey]?.[tier])
    .sort((a, b) => (from === "highest" ? Number(b) - Number(a) : Number(a) - Number(b)));

  const found: TIContainerInformationExtendedValid[] = [];
  const takenBays = new Set<IBayPattern>();
  const oddBays = block.getEnclosedBays().filter((b) => Number(b) % 2 === 1);

  for (const tier of tiersInStack) {
    const containers = (gridContent[stackKey]?.[tier]?.containers ??
      []) as TIContainerInformationExtendedValid[];

    containers.forEach((c) => {
      const bays = baysCoveredBy(c, block);

      if (bays.some((bay) => !takenBays.has(bay))) {
        found.push(c);
        bays.forEach((bay) => takenBays.add(bay));
      }
    });

    // Every bay of the block has its outermost container
    if (oddBays.length > 0 && oddBays.every((bay) => takenBays.has(bay))) break;
  }

  return found;
}
